/**
 * The window says what it holds (RFC 0346 S2).
 *
 * The window's renderer holds two repos: the tab repo on the main thread and
 * the shared worker's. Each keeps its documents in its own Automerge wasm
 * memory, and the browser's own counters never see wasm: `performance.memory`
 * is the JavaScript heap only, which is why every early look at the window
 * concluded memory was fine while the renderer sat at six gigabytes.
 *
 * So each realm catches its wasm memories as they are made (`trackWasm`, the
 * same move as the daemon's), and every 30 seconds posts what it holds to the
 * daemon's local server. The daemon reads the renderer's footprint from the OS
 * and writes one report for the whole window beside its own, so the Memory app
 * draws both and the parts add up to what Activity Monitor shows.
 *
 * A failed post is dropped, never retried or thrown: a window running without
 * a daemon has nobody to tell, and a diagnostic must not cost the window.
 */

import { stats } from '@automerge/automerge/slim'
import type { Repo } from '@automerge/automerge-repo/slim'
import { localServer } from '../../sdk/localServer'

/** Which repo in the window this is. */
export type Realm = 'tab' | 'worker'

/** What one realm holds, as posted to the daemon's `/memory/realm`. */
export interface RealmReport {
  realm: Realm
  /** This page's realm, apart from any other page's: each tab and worker
   *  instance reports under its own id. */
  instance: string
  /** The desktop window, or a browser tab on the same dev server. Only the
   *  window's own realms belong in the window's report. */
  host: 'desktop' | 'browser'
  at: number
  /** Each wasm memory by label. Never shrinks, so a high-water mark. */
  wasm: Record<string, number>
  /** The JavaScript heap, where the realm can see it: the main thread can,
   *  a shared worker cannot. */
  jsHeap: number | null
  documents: { loaded: number; ready: number; ops: number }
  /** The heaviest ready documents. */
  topDocuments: { id: string; ops: number }[]
  /** What this realm last did that memory alone cannot show, such as the
   *  worker's last catch-up after a reconnect. */
  notes?: Record<string, string>
}

interface WasmMemoryObject {
  readonly buffer: ArrayBuffer
}
interface WasmInstance {
  readonly exports: Record<string, unknown>
}
type InstanceCtor = new (module: unknown, imports?: unknown) => WasmInstance
type Instantiate = (
  source: unknown,
  imports?: unknown,
) => Promise<WasmInstance | { instance: WasmInstance }>
interface WasmGlobal {
  Instance: InstanceCtor
  instantiate: Instantiate
  Memory: new (...args: never[]) => WasmMemoryObject
}

const memories: { label: string; memory: WasmMemoryObject }[] = []

function keep(label: string, instance: WasmInstance): void {
  const wasm = (globalThis as unknown as { WebAssembly: WasmGlobal })
    .WebAssembly
  for (const value of Object.values(instance.exports)) {
    if (value instanceof wasm.Memory) {
      memories.push({ label, memory: value })
      return
    }
  }
}

/**
 * Run a wasm init with instantiation watched, and remember the memory it
 * makes. Restores both entry points afterwards, even when the init throws.
 */
export async function trackWasm<T>(
  label: string,
  init: () => T | Promise<T>,
): Promise<T> {
  const wasm = (globalThis as unknown as { WebAssembly: WasmGlobal })
    .WebAssembly
  const OriginalInstance = wasm.Instance
  const originalInstantiate = wasm.instantiate
  // A function, not an arrow: wasm-bindgen calls it with `new`.
  // biome-ignore lint/complexity/useArrowFunction: must be constructible
  const Tracked = function (module: unknown, imports?: unknown): WasmInstance {
    const instance = new OriginalInstance(module, imports)
    keep(label, instance)
    return instance
  } as unknown as InstanceCtor
  Tracked.prototype = OriginalInstance.prototype
  wasm.Instance = Tracked
  wasm.instantiate = async (source, imports) => {
    const result = await originalInstantiate.call(wasm, source, imports)
    keep(
      label,
      result instanceof OriginalInstance
        ? result
        : (result as { instance: WasmInstance }).instance,
    )
    return result
  }
  try {
    return await init()
  } finally {
    wasm.Instance = OriginalInstance
    wasm.instantiate = originalInstantiate
  }
}

const TOP_DOCUMENTS = 10

const notes: Record<string, string> = {}

/** Carry a line in this realm's next reports, stamped with the time. */
export function noteRealm(key: string, line: string): void {
  notes[key] = `${new Date().toISOString()} ${line}`
}

const INSTANCE = Math.random().toString(36).slice(2, 10)
const HOST: RealmReport['host'] = /Electron/.test(
  (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent ??
    '',
)
  ? 'desktop'
  : 'browser'

/** What this realm holds now. */
export function measureRealm(
  realm: Realm,
  repo: Pick<Repo, 'handles'>,
): RealmReport {
  const wasm: Record<string, number> = {}
  for (const { label, memory } of memories) {
    wasm[label] = (wasm[label] ?? 0) + memory.buffer.byteLength
  }
  const weights: { id: string; ops: number }[] = []
  let loaded = 0
  for (const [id, handle] of Object.entries(repo.handles)) {
    loaded++
    if (!handle.isReady()) continue
    try {
      weights.push({ id, ops: stats(handle.doc() as never).numOps })
    } catch {
      // Loaded but unreadable now: counted, not weighed.
    }
  }
  weights.sort((a, b) => b.ops - a.ops)
  const heap = (
    globalThis as unknown as {
      performance?: { memory?: { totalJSHeapSize?: number } }
    }
  ).performance?.memory?.totalJSHeapSize
  return {
    realm,
    instance: INSTANCE,
    host: HOST,
    at: Date.now(),
    wasm,
    jsHeap: typeof heap === 'number' ? heap : null,
    documents: {
      loaded,
      ready: weights.length,
      ops: weights.reduce((sum, w) => sum + w.ops, 0),
    },
    topDocuments: weights.slice(0, TOP_DOCUMENTS),
    ...(Object.keys(notes).length > 0 ? { notes: { ...notes } } : {}),
  }
}

export const REALM_REPORT_INTERVAL_MS = 30_000

/** Post this realm's report to the daemon every 30 seconds. */
export function startRealmReport(
  realm: Realm,
  repo: Pick<Repo, 'handles'>,
  post: (report: RealmReport) => Promise<unknown> = (report) =>
    fetch(`${localServer()}/memory/realm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report),
    }),
): () => void {
  const send = (): void => {
    try {
      void post(measureRealm(realm, repo)).catch(() => {})
    } catch {
      // Measuring must never cost the window.
    }
  }
  send()
  const timer = setInterval(send, REALM_REPORT_INTERVAL_MS)
  return () => clearInterval(timer)
}
